import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { KeyboardEvent, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { useTouchEditVisible } from '@/core/store/touchEdit/touchEditSelectors';

import type { DcrAttachment } from '../attachment.types';

import { attachmentThumbnailStyles as sx } from './AttachmentThumbnail.styles';

type Props = {
	attachment: DcrAttachment;
	onRemove?: (id: string) => void;
	onClick?: (attachment: DcrAttachment) => void;
	size?: 'compact' | 'large';
};

export const AttachmentThumbnail = ({
	attachment,
	onRemove,
	onClick,
	size = 'compact'
}: Props) => {
	const { t } = useTranslation();
	const touchEditVisible = useTouchEditVisible();
	const isUploading = attachment.status === 'uploading';
	const hasError = attachment.status === 'error';
	const clickable = !!onClick && !isUploading && !hasError;
	const isLarge = size === 'large';

	const handleClick = (e: MouseEvent) => {
		e.stopPropagation();
		if (clickable) onClick?.(attachment);
	};

	const handleRemove = (e: MouseEvent) => {
		e.stopPropagation();
		onRemove?.(attachment.id);
	};

	const handleKeyDown = (e: KeyboardEvent) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			e.stopPropagation();
			onClick?.(attachment);
		}
	};

	const thumbnail = (
		<Box
			sx={sx.root(clickable, isLarge)}
			onClick={handleClick}
			role={clickable ? 'button' : undefined}
			tabIndex={clickable ? 0 : undefined}
			onKeyDown={clickable ? handleKeyDown : undefined}
		>
			<Box sx={sx.imageClip}>
				<Box
					component='img'
					src={attachment.previewUrl}
					alt={attachment.name}
					sx={sx.image}
				/>

				{isUploading && (
					<Box sx={sx.uploadingOverlay}>
						<CircularProgress
							variant='determinate'
							value={attachment.progress}
							size={32}
							thickness={4}
						/>
					</Box>
				)}

				{hasError && (
					<Box sx={sx.errorOverlay}>
						<ErrorOutlineIcon fontSize='medium' />
					</Box>
				)}

				{isLarge && !isUploading && !hasError && (
					<Box sx={sx.hoverOverlay(touchEditVisible)} data-slot='hover-overlay'>
						<Box sx={sx.hoverTopRow}>
							{onRemove && (
								<IconButton
									size='small'
									sx={sx.hoverRemoveButton(touchEditVisible)}
									onClick={handleRemove}
									aria-label={t('Remove attachment')}
									data-slot='hover-remove-button'
								>
									<DeleteOutlineIcon fontSize='small' />
								</IconButton>
							)}
						</Box>
						<Typography sx={sx.hoverFileName}>{attachment.name}</Typography>
					</Box>
				)}
			</Box>

			{!isLarge && onRemove && (
				<IconButton
					size='small'
					sx={sx.removeButton}
					onClick={handleRemove}
					aria-label={t('Remove attachment')}
				>
					<CloseIcon />
				</IconButton>
			)}
		</Box>
	);

	return isLarge ? (
		thumbnail
	) : (
		<Tooltip title={hasError ? attachment.error : attachment.name}>
			{thumbnail}
		</Tooltip>
	);
};
